/**
 * Creates a spring easing function and its ideal duration.
 *
 * @param {object} opts
 * @param {number} opts.mass
 * @param {number} opts.stiffness
 * @param {number} opts.damping
 * @param {number} [opts.velocity=0]
 * @returns {[number, function]} [durationMs, easing(t: 0→1): number]
 */
export function createSpring({ mass, stiffness, damping, velocity = 0 }) {
  const w0   = Math.sqrt(stiffness / mass);
  const zeta = damping / (2 * Math.sqrt(stiffness * mass));
  const wd   = zeta < 1 ? w0 * Math.sqrt(1 - zeta * zeta) : 0;
  const b    = zeta < 1 ? (zeta * w0 - velocity) / wd : -velocity + w0;

  function solver(t) {
    if (zeta < 1) {
      t = Math.exp(-t * zeta * w0) * (Math.cos(wd * t) + b * Math.sin(wd * t));
    } else {
      t = (1 + b * t) * Math.exp(-t * w0);
    }
    return 1 - t;
  }

  const duration = (() => {
    const step = 1 / 6;
    let time = 0;
    while (true) {
      if (Math.abs(1 - solver(time)) < 0.001) {
        const restStart = time;
        let restSteps = 1;
        while (true) {
          time += step;
          if (Math.abs(1 - solver(time)) >= 0.001) break;
          restSteps++;
          if (restSteps === 16) return restStart;
        }
      }
      time += step;
    }
  })();

  return [duration * 1000, t => solver(duration * t)];
}
